// Language context for internationalization
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import en from "@/locales/en";
import id from "@/locales/id";
import ja from "@/locales/ja";
import ko from "@/locales/ko";
import zh from "@/locales/zh";
import de from "@/locales/de";
import fr from "@/locales/fr";
import es from "@/locales/es";

export type Language = "en" | "id" | "ja" | "ko" | "zh" | "de" | "fr" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof en;
}

const translations = {
  en,
  id,
  ja,
  ko,
  zh,
  de,
  fr,
  es,
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "portfolio.language";

const SUPPORTED = Object.keys(translations) as Language[];

const isSupported = (value: unknown): value is Language =>
  typeof value === "string" && (SUPPORTED as string[]).includes(value);

/**
 * Resolves the initial language: a previous explicit choice wins, then the browser's
 * preferred languages, then English.
 *
 * Every branch is guarded — localStorage throws in private windows and when site data is
 * blocked, and this runs during the first render.
 */
const detectLanguage = (): Language => {
  if (typeof window === "undefined") return "en";

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    // Storage unavailable — fall through to the browser preference.
  }

  try {
    const preferred = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];

    for (const tag of preferred) {
      // Match the base subtag so "ja-JP" and "zh-Hans-CN" resolve to "ja" and "zh".
      const base = tag?.split("-")[0]?.toLowerCase();
      if (isSupported(base)) return base;
    }
  } catch {
    // navigator unavailable — use the default.
  }

  return "en";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(detectLanguage);

  // Remember the choice so it survives reloads and navigation, and keep the document
  // language in sync for screen readers and correct hyphenation.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // Nothing to do: the session still works, it just will not be remembered.
    }

    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language] as typeof en;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
